(() => {
  const MIN_COMPLETION_MS = 1800;
  const STEPS = [
    'received',
    'diagnosing',
    'awaiting approval',
    'approved / awaiting parts',
    'repairing',
    'quality check',
    'ready'
  ];

  const text = (value) => String(value == null ? '' : value).trim();
  const field = (fd, name) => text(fd.get(name));

  const canonicalService = (value) => {
    const raw = text(value);
    const normalised = raw.toLowerCase();
    const hasWaterproofing = normalised.includes('waterproof') || normalised.includes('water proof');
    const hasGeneralService = normalised.includes('general pev check')
      || normalised.includes('general service')
      || normalised.includes('annual board service')
      || normalised.includes('annual electric skateboard service');
    const hasBatteryRebalance = normalised.includes('battery rebalance')
      || normalised.includes('battery re-balance')
      || normalised.includes('battery balance');
    const hasBoostedRlod = normalised.includes('boosted') && normalised.includes('rlod');

    if (hasWaterproofing && hasGeneralService) {
      return 'General PEV Check & Service + PEV Waterproofing';
    }
    if (hasBatteryRebalance) return 'Battery Rebalance';
    if (hasBoostedRlod) return 'Boosted RLOD Clear';
    if (hasWaterproofing) return 'PEV Waterproofing';
    if (hasGeneralService) return 'General PEV Check & Service';
    return 'Diagnostic Service Labour';
  };

  const createClientReference = () => {
    const now = new Date();
    const date = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('');
    const random = Math.random().toString(36).slice(2, 7).toUpperCase();
    return `PEV-${date}-${random}`;
  };

  const requestOpaque = async (endpoint, payload) => {
    if (!endpoint) throw new Error('Workshop app endpoint is not configured.');
    await fetch(endpoint, {
      method: 'POST',
      mode: 'no-cors',
      credentials: 'omit',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({ ...payload, deliveryMode: 'browser_compatibility' }),
      keepalive: true
    });
    return { queued: true, clientReference: payload.clientReference };
  };

  const sendShopifyBackupSilently = (form, note) => {
    try {
      const frameName = `pev-workshop-backup-${Date.now()}`;
      const frame = document.createElement('iframe');
      frame.name = frameName;
      frame.hidden = true;
      frame.setAttribute('aria-hidden', 'true');
      const clone = form.cloneNode(true);
      clone.target = frameName;
      clone.removeAttribute('id');
      clone.dataset.nativeFallback = 'true';
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'contact[App delivery]';
      hidden.value = note;
      clone.appendChild(hidden);
      document.body.appendChild(frame);
      document.body.appendChild(clone);
      HTMLFormElement.prototype.submit.call(clone);
      window.setTimeout(() => { clone.remove(); frame.remove(); }, 15000);
    } catch (error) {
      console.warn('Shopify backup could not be sent silently:', error);
    }
  };

  const setMessage = (node, message, state = 'pending') => {
    if (!node) return;
    node.hidden = false;
    node.textContent = message;
    node.dataset.state = state;
  };

  const requestJson = async (endpoint, payload) => {
    if (!endpoint) throw new Error('Workshop app endpoint is not configured.');
    const response = await fetch(endpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    let result = null;
    try { result = await response.json(); } catch (_) { result = null; }
    if (!response.ok || result?.success === false) {
      throw new Error(result?.error || result?.message || `Workshop app returned ${response.status}`);
    }
    return result || {};
  };

  const bookingPayload = (form, section) => {
    const fd = new FormData(form);
    const name = field(fd, 'contact[name]');
    const email = field(fd, 'contact[email]');
    const phone = field(fd, 'contact[phone]');
    const pevType = field(fd, 'contact[PEV type]');
    const service = canonicalService(field(fd, 'contact[Service required]'));
    const makeModel = field(fd, 'contact[PEV make and model]');
    const items = field(fd, 'contact[Items being left]');
    const issue = field(fd, 'contact[body]');
    const modifications = field(fd, 'contact[Modifications]');
    const orderNumber = field(fd, 'contact[Order number]');
    const serialNumber = field(fd, 'contact[Serial number]');
    const handoverMethod = field(fd, 'contact[Handover method]');
    const returnMethod = field(fd, 'contact[Return method]');
    const returnPostcode = field(fd, 'contact[Return postcode]');
    const preferredDate = field(fd, 'contact[Preferred date]');
    const additionalNotes = field(fd, 'contact[Anything else]');
    const preAuthorisedAmount = field(fd, 'contact[Pre-authorised spend limit]');
    const preAuthorisedLabel = preAuthorisedAmount
      ? `£${preAuthorisedAmount} total workshop spend`
      : 'Diagnostic only - contact customer before further work';
    const preAuthorisationNote = `Customer pre-authorisation: ${preAuthorisedLabel}. Contact the customer before the total exceeds this limit.`;
    const concernForWorker = [issue, preAuthorisationNote].filter(Boolean).join('\n\n');
    // API 3.5 compatibility: the current Worker builds its temporary draft line
    // from `items`. Including the canonical service title here lets Build 147+
    // show the selected service and replace the temporary line with the real
    // Shopify product on acceptance. `itemsBeingLeft` remains the clean value.
    const apiItems = [items, service].filter(Boolean).join(' | ');

    return {
      requestType: 'service_booking',
      clientReference: createClientReference(),
      source: 'Powered Up PEV website workshop booking',
      submittedAt: new Date().toISOString(),
      shop: section.dataset.shopDomain || window.location.hostname,
      pageUrl: window.location.href,
      name,
      email,
      phone,
      pevType,
      itemType: pevType,
      service,
      serviceRequired: service,
      service_required: service,
      requestedService: service,
      requested_service: service,
      serviceType: service,
      service_type: service,
      items: apiItems,
      itemsLeft: apiItems,
      items_left: apiItems,
      itemsBeingLeft: items,
      items_being_left: items,
      rawItemsBeingLeft: items,
      makeModel,
      make_model: makeModel,
      issue,
      concern: concernForWorker,
      message: concernForWorker,
      problem: concernForWorker,
      preAuthorisedAmount,
      pre_authorised_amount: preAuthorisedAmount,
      preAuthorizationAmount: preAuthorisedAmount,
      pre_authorization_amount: preAuthorisedAmount,
      authorisedSpendLimit: preAuthorisedAmount,
      approvedSpendLimit: preAuthorisedAmount,
      modifications,
      orderNumber,
      serialNumber,
      handoverMethod,
      returnMethod,
      return_method: returnMethod,
      returnPostcode,
      return_postcode: returnPostcode,
      preferredDate,
      additionalNotes,
      policyAccepted: field(fd, 'contact[Service policy agreement]') === 'Agreed',
      diagnosticChargeAcknowledged: true,
      tags: ['Website Service Request', 'Needs Review', 'Service'],
      customer: { name, email, phone },
      contact: {
        name,
        email,
        phone,
        pev_type: pevType,
        service_required: service,
        service_type: service,
        requested_service: service,
        items_being_left: items,
        make_and_model: makeModel,
        issue,
        concern: concernForWorker,
        pre_authorised_amount: preAuthorisedAmount,
        authorised_spend_limit: preAuthorisedAmount,
        modifications,
        order_number: orderNumber,
        serial_number: serialNumber,
        handover_method: handoverMethod,
        return_method: returnMethod,
        return_postcode: returnPostcode,
        preferred_date: preferredDate,
        additional_notes: additionalNotes
      },
      draftNote: [
        `Service: ${service}`,
        `Item(s): ${items || `${pevType} - ${makeModel}`}`,
        `Order: ${orderNumber || 'Not supplied'}`,
        `Serial: ${serialNumber || 'Not supplied'}`,
        `Issue: ${issue}`,
        `Pre-authorised spend limit: ${preAuthorisedLabel}`,
        `Authorisation instruction: Contact customer before the total exceeds this limit`,
        `Modifications: ${modifications || 'None declared'}`,
        `Handover: ${handoverMethod}`,
        `Return: ${returnMethod}`,
        `Postcode: ${returnPostcode || 'Not supplied'}`,
        `Preferred date: ${preferredDate || 'No preference'}`,
        `Notes: ${additionalNotes || 'None'}`
      ].join('\n')
    };
  };

  const initialiseBooking = (section) => {
    const formFields = section.querySelector('[data-workshop-booking-form]');
    const form = formFields?.closest('form');
    if (!form || form.dataset.initialised === 'true') return;
    if (form.dataset.nativeFallback === 'true') return;
    form.dataset.initialised = 'true';
    const started = form.querySelector('[name="form_started_at"]');
    if (started) started.value = String(Date.now());

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const fd = new FormData(form);
      if (field(fd, 'website')) return;
      const startValue = Number(field(fd, 'form_started_at'));
      if (Number.isFinite(startValue) && Date.now() - startValue < MIN_COMPLETION_MS) return;

      const message = form.querySelector('[data-workshop-message]');
      const button = form.querySelector('button[type="submit"]');
      const original = button?.innerHTML;
      if (button) { button.disabled = true; button.textContent = 'Sending to workshop app…'; }
      setMessage(message, 'Creating your workshop job…', 'pending');

      try {
        const payload = bookingPayload(form, section);
        const result = await requestJson(section.dataset.endpoint, payload);
        const reference = text(result.draftOrderName || result.jobReference || result.reference || result.orderName || result.id) || 'Reference pending';
        const success = section.querySelector('[data-booking-success]');
        const requestPanel = section.querySelector('[data-booking-request-panel]');
        const referenceNode = section.querySelector('[data-booking-reference]');
        const trackLink = section.querySelector('[data-booking-track-link]');
        if (referenceNode) referenceNode.textContent = reference;
        if (trackLink) {
          const url = new URL(section.dataset.trackUrl || '/pages/track-a-service-job', window.location.origin);
          url.searchParams.set('ref', reference);
          url.searchParams.set('email', payload.email);
          trackLink.href = url.toString();
        }
        try { sessionStorage.setItem('pevWorkshopReference', reference); sessionStorage.setItem('pevWorkshopEmail', payload.email); } catch (_) {}
        form.reset();
        if (requestPanel) requestPanel.hidden = true;
        if (success) success.hidden = false;
        success?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        console.error('Workshop booking confirmation failed:', error);
        const payload = bookingPayload(form, section);
        setMessage(message, 'The app did not return a browser-readable confirmation. Retrying through the compatibility route…', 'pending');
        try {
          const queued = await requestOpaque(section.dataset.endpoint, payload);
          const reference = queued.clientReference || payload.clientReference || 'Pending confirmation';
          sendShopifyBackupSilently(
            form,
            `Workshop endpoint compatibility delivery attempted. Client reference: ${reference}. Standard error: ${text(error?.message) || 'Unknown error'}`
          );
          const success = section.querySelector('[data-booking-success]');
          const requestPanel = section.querySelector('[data-booking-request-panel]');
          const referenceNode = section.querySelector('[data-booking-reference]');
          const headingNode = success?.querySelector('h2');
          const bodyNode = success?.querySelector('p:not(.pev-booking__kicker)');
          const trackLink = section.querySelector('[data-booking-track-link]');
          if (referenceNode) referenceNode.textContent = `${reference} (pending app confirmation)`;
          if (headingNode) headingNode.textContent = 'Booking sent for app processing';
          if (bodyNode) bodyNode.textContent = 'A browser-safe delivery was sent to the workshop endpoint and Shopify was sent a backup copy. Your confirmed workshop reference will be emailed after the app processes the request.';
          if (trackLink) {
            const url = new URL(section.dataset.trackUrl || '/pages/track-a-service-job', window.location.origin);
            url.searchParams.set('ref', reference);
            url.searchParams.set('email', payload.email);
            trackLink.href = url.toString();
          }
          try { sessionStorage.setItem('pevWorkshopReference', reference); sessionStorage.setItem('pevWorkshopEmail', payload.email); } catch (_) {}
          form.reset();
          if (requestPanel) requestPanel.hidden = true;
          if (success) success.hidden = false;
          success?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (retryError) {
          console.error('Workshop compatibility delivery failed:', retryError);
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'contact[App delivery]';
          hidden.value = `Workshop app delivery failed. Standard error: ${text(error?.message) || 'Unknown error'}. Compatibility error: ${text(retryError?.message) || 'Unknown error'}. Submitted through Shopify contact fallback.`;
          form.appendChild(hidden);
          form.dataset.nativeFallback = 'true';
          setMessage(message, 'The workshop endpoint could not be reached. Sending the same form through Shopify instead…', 'error');
          HTMLFormElement.prototype.submit.call(form);
          return;
        }
      } finally {
        if (button) { button.disabled = false; button.innerHTML = original || 'Send booking request'; }
      }
    });
  };

  const normaliseStatus = (result, reference) => {
    const data = result.job || result.data || result.statusRecord || result;
    return {
      reference: text(data.reference || data.jobReference || data.draftOrderName || data.orderName || reference),
      status: text(data.status || data.stage || data.jobStatus || 'Workshop update available'),
      stageIndex: Number.isFinite(Number(data.stageIndex)) ? Number(data.stageIndex) : null,
      item: text(data.item || data.items || data.makeModel || data.make_model || data.product || 'Not supplied'),
      service: text(data.service || data.serviceRequired || data.service_required || 'Not supplied'),
      updated: text(data.lastUpdated || data.updatedAt || data.updated_at || data.modifiedAt || 'Not supplied'),
      returnMethod: text(data.returnMethod || data.return_method || data.fulfilmentPreference || 'Not set'),
      message: text(data.message || data.latestUpdate || data.latest_note || data.note || 'No workshop note has been published.'),
      invoiceUrl: text(data.invoiceUrl || data.invoice_url || data.paymentUrl || data.payment_url),
      trackingUrl: text(data.trackingUrl || data.tracking_url || data.shippingUrl || data.shipping_url)
    };
  };

  const inferStageIndex = (status) => {
    const value = status.toLowerCase();
    if (value.includes('ready') || value.includes('returned') || value.includes('dispatch') || value.includes('complete')) return 6;
    if (value.includes('invoice') || value.includes('payment')) return 5;
    if (value.includes('quality')) return 4;
    if (value.includes('repair') || value.includes('parts') || value.includes('approved')) return 3;
    if (value.includes('approval') || value.includes('quote')) return 2;
    if (value.includes('diagnos') || value.includes('inspect')) return 1;
    return 0;
  };

  const renderStatus = (section, status) => {
    const result = section.querySelector('[data-status-result]');
    if (!result) return;
    const set = (selector, value) => { const node = result.querySelector(selector); if (node) node.textContent = value || '—'; };
    set('[data-status-title]', status.status);
    set('[data-status-reference]', status.reference);
    set('[data-status-pill]', status.status);
    set('[data-status-item]', status.item);
    set('[data-status-service]', status.service);
    set('[data-status-updated]', status.updated);
    set('[data-status-return]', status.returnMethod);
    set('[data-status-message-text]', status.message);

    const progress = result.querySelector('[data-status-progress]');
    if (progress) {
      const stage = status.stageIndex == null ? inferStageIndex(status.status) : Math.max(0, Math.min(6, status.stageIndex));
      progress.innerHTML = '';
      STEPS.forEach((_, index) => {
        const bar = document.createElement('span');
        if (index <= stage) bar.classList.add('is-complete');
        progress.appendChild(bar);
      });
    }

    const invoice = result.querySelector('[data-status-invoice]');
    if (invoice) { invoice.hidden = !status.invoiceUrl; if (status.invoiceUrl) invoice.href = status.invoiceUrl; }
    const shipping = result.querySelector('[data-status-shipping]');
    if (shipping) { shipping.hidden = !status.trackingUrl; if (status.trackingUrl) shipping.href = status.trackingUrl; }
    result.hidden = false;
    result.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const initialiseTracking = (section) => {
    const form = section.querySelector('[data-workshop-status-form]');
    if (!form || form.dataset.initialised === 'true') return;
    form.dataset.initialised = 'true';
    const params = new URLSearchParams(window.location.search);
    const referenceInput = form.querySelector('[name="reference"]');
    const emailInput = form.querySelector('[name="email"]');
    let storedReference = '';
    let storedEmail = '';
    try { storedReference = sessionStorage.getItem('pevWorkshopReference') || ''; storedEmail = sessionStorage.getItem('pevWorkshopEmail') || ''; } catch (_) {}
    if (referenceInput) referenceInput.value = params.get('ref') || storedReference;
    if (emailInput) emailInput.value = params.get('email') || storedEmail;
    const updateReference = section.querySelector('[data-update-reference]');
    if (updateReference && referenceInput?.value) updateReference.value = referenceInput.value;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const reference = text(referenceInput?.value);
      const email = text(emailInput?.value);
      const message = form.querySelector('[data-status-message]');
      const button = form.querySelector('button[type="submit"]');
      const original = button?.innerHTML;
      if (button) { button.disabled = true; button.textContent = 'Checking workshop app…'; }
      setMessage(message, 'Looking up the workshop record…', 'pending');
      try {
        const result = await requestJson(section.dataset.statusEndpoint, { requestType: 'service_status', reference, email, shop: window.location.hostname });
        const status = normaliseStatus(result, reference);
        renderStatus(section, status);
        if (updateReference) updateReference.value = status.reference || reference;
        message.hidden = true;
      } catch (error) {
        console.error('Workshop status lookup failed:', error);
        setMessage(message, 'We could not retrieve a live workshop record. Check the reference and email, or use the update form below so the workshop can reply.', 'error');
        if (updateReference) updateReference.value = reference;
      } finally {
        if (button) { button.disabled = false; button.innerHTML = original || 'Check job status'; }
      }
    });
  };



  const initialiseUpdateForm = (section) => {
    const form = section.querySelector('.pev-track__update-form');
    if (!form || form.dataset.initialised === 'true') return;
    form.dataset.initialised = 'true';
    const endpoint = text(section.dataset.updateEndpoint);
    if (!endpoint) return;

    form.addEventListener('submit', async (event) => {
      if (form.dataset.nativeFallback === 'true') return;
      event.preventDefault();
      if (!form.reportValidity()) return;
      const fd = new FormData(form);
      const payload = {
        requestType: 'service_update',
        source: 'Powered Up PEV track service job page',
        submittedAt: new Date().toISOString(),
        shop: window.location.hostname,
        pageUrl: window.location.href,
        reference: field(fd, 'contact[Job reference]'),
        jobReference: field(fd, 'contact[Job reference]'),
        name: field(fd, 'contact[name]'),
        email: field(fd, 'contact[email]'),
        reason: field(fd, 'contact[Request type]'),
        requestReason: field(fd, 'contact[Request type]'),
        message: field(fd, 'contact[body]'),
        tags: ['Workshop Update', 'Service Job'],
        customer: {
          name: field(fd, 'contact[name]'),
          email: field(fd, 'contact[email]')
        }
      };
      const message = form.querySelector('[data-update-message]');
      const button = form.querySelector('button[type="submit"]');
      const original = button?.innerHTML;
      if (button) { button.disabled = true; button.textContent = 'Sending to workshop app…'; }
      setMessage(message, 'Adding the update to the workshop record…', 'pending');
      try {
        await requestJson(endpoint, payload);
        setMessage(message, 'Your update has been added to the workshop app.', 'success');
        const reference = payload.reference;
        form.reset();
        const refInput = form.querySelector('[data-update-reference]');
        if (refInput) refInput.value = reference;
      } catch (error) {
        console.error('Workshop update app delivery failed:', error);
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'contact[App delivery]';
        hidden.value = 'Workshop app delivery failed; sent through Shopify contact fallback';
        form.appendChild(hidden);
        form.dataset.nativeFallback = 'true';
        setMessage(message, 'The app connection was unavailable. Sending the same update through Shopify instead…', 'error');
        HTMLFormElement.prototype.submit.call(form);
        return;
      } finally {
        if (button) { button.disabled = false; button.innerHTML = original || 'Send update request'; }
      }
    });
  };

  document.querySelectorAll('[data-workshop-booking-section]').forEach(initialiseBooking);
  document.querySelectorAll('[data-workshop-tracking-section]').forEach((section) => { initialiseTracking(section); initialiseUpdateForm(section); });
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-workshop-booking-section]').forEach(initialiseBooking);
    event.target.querySelectorAll?.('[data-workshop-tracking-section]').forEach((section) => { initialiseTracking(section); initialiseUpdateForm(section); });
  });
})();
